<!-- src/apps/secret/components/canonical/SecretConfirmationForm.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import NeedHelpModal from '@/shared/components/modals/NeedHelpModal.vue';
  import SecretRecipientHelpContent from '@/apps/secret/components/SecretRecipientHelpContent.vue';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import type { Secret, SecretDetails } from '@/schemas/shapes/v3/secret';
  import { ref, computed } from 'vue';

  const bootstrapStore = useBootstrapStore();
  const helpEnabled = computed(() => bootstrapStore.ui?.help?.enabled ?? true);

  interface Props {
    secretIdentifier: string;
    record: Secret | null;
    details: SecretDetails | null;
    isSubmitting: boolean;
    error: unknown;
  }

  const props = defineProps<Props>();
  const emit = defineEmits(['user-confirmed']);
  const { t } = useI18n();

  const passphrase = ref('');

  // Generate unique IDs for ARIA attributes based on secretIdentifier
  const formHeadingId = computed(() => `form-heading-${props.secretIdentifier}`);
  const passphraseInputId = computed(() => `passphrase-${props.secretIdentifier}`);
  const passphraseHeadingId = computed(() => `passphrase-heading-${props.secretIdentifier}`);
  const passphraseDescriptionId = computed(() => `passphrase-description-${props.secretIdentifier}`);

  // Determine the primary status message based on record state
  const statusMessage = computed(() => {
    // Secret has passphrase protection
    if (props.record?.has_passphrase) {
      return t('web.shared.requires_passphrase');
    }
    // Verification secret (no passphrase)
    if (props.record?.verification) {
      return t('web.COMMON.click_to_verify');
    }
    // Regular secret without passphrase - just needs confirmation click
    return t('web.COMMON.click_to_continue');
  });

  // Handle form submission
  const submitForm = async () => {
    emit('user-confirmed', passphrase.value);
  };
</script>

<template>
  <div
    :class="['w-full', 'rounded-lg bg-white p-8 dark:bg-gray-800']"
    role="region"
    :aria-labelledby="formHeadingId">
    <!-- Header section with title, status, and help link -->
    <div class="mb-4 flex items-start justify-between">
      <div>
        <h1
          :id="formHeadingId"
          class="text-xl font-bold text-gray-800 dark:text-gray-200">
          {{ statusMessage }}
        </h1>
        <div
          v-if="!record?.has_passphrase"
          class="mt-1 text-base text-gray-600 dark:text-gray-400"
          role="status"
          aria-live="polite">
          {{ t('web.COMMON.careful_only_see_once') }}
        </div>
      </div>

      <!-- Help Modal Trigger positioned to the right -->
      <NeedHelpModal
        v-if="helpEnabled"
        link-icon-name="question-mark-circle-16-solid"
        link-text-label="">
        <!-- prettier-ignore-attribute class -->
        <button
          type="button"
          class="ml-4 text-sm font-medium
            text-brand-600 hover:text-brand-500
            focus:underline focus:outline-none dark:text-brand-400 dark:hover:text-brand-300"
          data-testid="secret-help-modal-trigger">
          {{ t('web.COMMON.need_help') }}?
        </button>
        <template #content>
          <SecretRecipientHelpContent />
        </template>
      </NeedHelpModal>
    </div>

    <form
      @submit.prevent="submitForm"
      class="space-y-6"
      :aria-labelledby="record?.has_passphrase ? passphraseHeadingId : undefined"
      :aria-describedby="record?.has_passphrase ? passphraseDescriptionId : undefined"
      data-testid="secret-confirmation-form">
      <!-- Hidden username field for accessibility/password managers -->
      <input
        type="text"
        name="username"
        autocomplete="username"
        class="sr-only"
        tabindex="-1"
        aria-hidden="true" />

      <!-- Conditional Passphrase Section -->
      <div
        v-if="record?.has_passphrase"
        class="space-y-2">
        <div>
          <label
            :for="passphraseInputId"
            class="sr-only">
            {{ t('web.COMMON.enter_passphrase_here') }}
          </label>
          <!-- prettier-ignore-attribute class -->
          <input
            v-model="passphrase"
            :id="passphraseInputId"
            type="password"
            name="passphrase"
            class="w-full rounded-md border border-gray-300 px-3 py-2
              focus:outline-none focus:ring-2 focus:ring-brand-500
              dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            autocomplete="current-password"
            :placeholder="t('web.COMMON.enter_passphrase_here')"
            aria-required="true"
            :aria-invalid="error ? 'true' : undefined"
            :aria-errormessage="error ? 'passphrase-error' : undefined"
            :aria-describedby="passphraseDescriptionId"
            data-testid="secret-reveal-passphrase-input" />
        </div>
        <p
          v-if="error"
          id="passphrase-error"
          class="mt-1 text-sm text-red-600 dark:text-red-400"
          role="alert"
          data-testid="secret-reveal-error">
          {{ String(error) }}
        </p>
      </div>

      <!-- Without a passphrase field the error still needs a place: a failed
           reveal (e.g. a refused verification, #4831) otherwise drops back to
           the same button with no explanation. -->
      <p
        v-else-if="error"
        class="text-sm text-red-600 dark:text-red-400"
        role="alert"
        data-testid="secret-reveal-error">
        {{ String(error) }}
      </p>

      <!-- Submission Button -->
      <button
        type="submit"
        :disabled="isSubmitting"
        :class="[
          'w-full rounded-md bg-brand-500 px-6 py-3 text-2xl font-semibold text-white transition duration-150 ease-in-out',
          'hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-600 dark:hover:bg-brand-600 dark:focus:ring-brand-400',
        ]"
        data-testid="secret-reveal-submit">
        {{ isSubmitting ? t('web.COMMON.submitting') : t('web.COMMON.click_to_continue') }}
      </button>
    </form>
  </div>
</template>
